import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import {
    IAttributeConfData,
    ProductConfigurableOptionsComponentDef
} from '@df/catalog-ui/product/configurable-options/product-configurable-options.component.def';
import { IProductDataV3 } from '@df/catalog/product/product';
import { Status } from '@df/core/status';
import { FindMediaByLabellPipe } from '@df/ui/common/find-media-by-label.pipe';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { ICmsModalService } from '@icms/client/assets/modal/icms-modal.service';
import compact from 'lodash-es/compact';
import difference from 'lodash-es/difference';
import { SwatchComponent } from '../../attribute/swatch/swatch.component';
import { NCmsProductSizeGuide } from '../cms/product-size-guide/cms-product-size-guide.component';
import { Product } from '../product.decorator';
import { ProductViewSamplesComponent } from '../view-samples/product-view-samples.component';

@Component({
    selector: 'product-view-configurable-options',
    templateUrl: 'product-view-configurable-options.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, SwatchComponent, HrefComponent, FindMediaByLabellPipe, ProductViewSamplesComponent, TranslationModule, IfSizeDirective]
})
export class ProductViewConfigurableOptionsComponent extends ProductConfigurableOptionsComponentDef implements OnInit {
    /**
     * Cms modal service
     */
    private cmsModalService = inject(ICmsModalService);
    @Input()
    override set product(product: Product<IProductDataV3>) {
        super.product = product;
    }

    /**
     * Gets current product
     */
    override get product(): Product<IProductDataV3> {
        return super.product as Product<IProductDataV3>;
    }
    @Output()
    crossellProductClick = new EventEmitter<Product<IProductDataV3>>();
    @Input()
    noCrossellLink = false;
    @Input()
    isHuge = true;
    @Input()
    upsellVariant: boolean = false;
    /**
     * Size gide modal open
     */
    readonly sizeGuideStatus = new Status();
    /**
     * Visible attributes list
     */
    visibleAttributes?: IAttributeConfData[];
    /**
     * If has size guide
     */
    hasSizeGuide(attribute: IAttributeConfData) {
        return attribute.code === 'size' && (this.parent as Product).sizeGuideVariant;
    }
    /**
     * Opens size guide
     */
    openSizeGuide(visuallyHidden) {
        this.cmsModalService.open(this.sizeGuideStatus, NCmsProductSizeGuide.BLOCK_ID, (this.product as Product).sizeGuideVariant, {
            visuallyHidden
        });
    }
    /**
     * Initialise size guide for prerender
     */
    override ngOnInit(): void {
        super.ngOnInit();
        if ((this.parent as Product).sizeGuideVariant && this.app.client.isPrerender) {
            this.openSizeGuide(true);
        }
    }
    /**
     * Make sure attrs are ordered
     */
    protected override setAttributes(attributes: IAttributeConfData[] | undefined) {
        if (!attributes) {
            attributes = [];
        }
        const orderedAttrs = ['color', 'size'].map(i => attributes?.find(a => a.code === i)).filter(i => !!i);
        attributes = compact([...orderedAttrs, ...difference(attributes, orderedAttrs)]);
        this.visibleAttributes = this.preselectSingles ? attributes?.filter(i => i.options.length > 1) : attributes;
        super.setAttributes(attributes);
    }
    trackByProductId(index: number, product: Product): number {
        return product.id;
    }
}
